// src/pages/battle/BattleMap.hooks.js
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  UnitType,
  executeShooting,
  applyShootingResult,
  executeMove,
  applyMoveResult,
  canMoveTo,
  refreshActionPoints,
  checkVictoryConditions,
  createCombatHelpers
} from '../../utils/combatSystem';
import {
  initializeUnitWithClass,
  executeAbility,
  applyAbilityEffect,
  putAbilityOnCooldown,
  decrementAbilityCooldowns,
  canUseAbility
} from '../../utils/skillSystem';
import { executeEnemyAction, applyEnemyAction } from '../../utils/enemyAI';
import { generateMap, initializeBattleUnits } from '../../utils/mapGeneration';
import { DEFAULT_MISSION, DEFAULT_SQUAD, XP_VALUES, KILL_BONUS_XP } from './Battlemap.constants';

export const useBattleSetup = (theme) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { mission, squad: passedSquad, campaignId } = location.state || {};
  
  const activeTheme = theme || params.themeName || 'vanilla';
  const activeMission = mission || DEFAULT_MISSION;
  const activeSquad = passedSquad || DEFAULT_SQUAD;

  return {
    navigate,
    location, // ADDED: Return location so it can be passed to other hooks
    activeTheme,
    activeMission,
    activeSquad,
    campaignId
  };
};

export const useBattleState = (activeMission, activeSquad) => {
  const [map] = useState(() => generateMap());
  const [units, setUnits] = useState(() => {
    const baseUnits = initializeBattleUnits(activeMission, activeSquad);
    return baseUnits.map(unit => {
      if (unit.type === UnitType.SOLDIER) {
        const squadMember = activeSquad.find(s => s.id === unit.id);
        return initializeUnitWithClass(
          unit,
          squadMember?.classId || 'ranger',
          squadMember?.unlockedSkills || []
        );
      }
      return unit;
    });
  });
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [mode, setMode] = useState('move');
  const [selectedAbility, setSelectedAbility] = useState(null);
  const [turn, setTurn] = useState(UnitType.SOLDIER);
  const [turnCount, setTurnCount] = useState(1);
  const [log, setLog] = useState([
    `Mission started: ${activeMission.name}`,
    `Location: ${activeMission.location}`,
    'Deploy your squad tactically.'
  ]);
  const [gameOver, setGameOver] = useState(false);
  const [victory, setVictory] = useState(false);

  const addLog = useCallback((msg) => setLog(prev => [...prev.slice(-6), msg]), []);

  return {
    map,
    units,
    setUnits,
    selectedUnit,
    setSelectedUnit,
    mode,
    setMode,
    selectedAbility,
    setSelectedAbility,
    turn,
    setTurn,
    turnCount,
    setTurnCount,
    log,
    addLog,
    gameOver,
    setGameOver,
    victory,
    setVictory
  };
};

export const useBattleActions = (battleState, map) => {
  const {
    units,
    setUnits,
    selectedUnit,
    setSelectedUnit,
    mode,
    setMode,
    selectedAbility,
    setSelectedAbility,
    turn,
    setTurn,
    turnCount,
    setTurnCount,
    addLog,
    gameOver
  } = battleState;

  const { getUnitAt, hasLineOfSight, getCoverBonus } = createCombatHelpers(map, units);

  const endTurn = useCallback(() => {
    const newTurn = turn === UnitType.SOLDIER ? UnitType.ENEMY : UnitType.SOLDIER;
    setUnits(prev => {
      let updated = refreshActionPoints(prev, newTurn);
      updated = decrementAbilityCooldowns(updated);
      return updated;
    });
    setTurn(newTurn);
    setSelectedUnit(null);
    setMode('move');
    setSelectedAbility(null);
    
    if (newTurn === UnitType.SOLDIER) {
      setTurnCount(prev => prev + 1);
      addLog(`--- Turn ${turnCount + 1} ---`);
    }
    addLog(`${newTurn === UnitType.SOLDIER ? '🔵 Your' : '🔴 Enemy'} turn begins`);
  }, [turn, turnCount, addLog, setUnits, setTurn, setSelectedUnit, setMode, setSelectedAbility, setTurnCount]);

  const handleAbilitySelect = useCallback((ability) => {
    if (!selectedUnit) return;
    
    const check = canUseAbility(selectedUnit, ability, selectedUnit.ap);
    if (!check.canUse) {
      if (check.reason === 'insufficient_ap') {
        addLog('⚠️ Not enough AP to use that ability!');
      } else if (check.reason === 'cooldown') {
        addLog(`⚠️ Ability on cooldown (${check.remaining} turns remaining)`);
      } else if (check.reason === 'no_uses_remaining') {
        addLog('⚠️ No uses remaining for this mission');
      }
      return;
    }

    setSelectedAbility(ability);
    setMode('ability');
  }, [selectedUnit, addLog, setSelectedAbility, setMode]);

  const handleTileClick = useCallback((x, y) => {
    if (gameOver || turn !== UnitType.SOLDIER) return;

    if (!selectedUnit) {
      const unit = getUnitAt(x, y);
      if (unit && unit.type === turn && unit.ap > 0) {
        setSelectedUnit(unit);
        setMode('move');
        setSelectedAbility(null);
        addLog(`Selected ${unit.name}`);
      }
      return;
    }

    if (mode === 'move') {
      const moveValidation = canMoveTo(selectedUnit, x, y, getUnitAt);
      if (moveValidation.valid) {
        const moveResult = executeMove(selectedUnit, x, y);
        setUnits(prev => applyMoveResult(moveResult, prev));
        addLog(moveResult.message);
        setSelectedUnit(null);
      } else if (moveValidation.reason === 'occupied') {
        addLog('⚠️ Tile is occupied!');
      } else if (moveValidation.reason === 'too_far') {
        addLog('⚠️ Not enough AP to reach that position!');
      }
    } else if (mode === 'shoot') {
      const target = getUnitAt(x, y);
      if (!target) return addLog('⚠️ No target at that position!');
      if (target.type === selectedUnit.type) return addLog('⚠️ Cannot target friendly units!');
      if (selectedUnit.ap < 1) return addLog('⚠️ Not enough AP to shoot!');

      const shootResult = executeShooting(selectedUnit, target, x, y, map);
      if (shootResult.success) {
        setUnits(prev => applyShootingResult(shootResult, prev));
        addLog(shootResult.message);
        if (shootResult.killMessage) addLog(shootResult.killMessage);
      } else {
        addLog(shootResult.message);
      }
      setSelectedUnit(null);
    } else if (mode === 'ability' && selectedAbility) {
      const target = getUnitAt(x, y);
      
      const needsTarget = ['medkit', 'revival_protocol', 'deadeye', 'disabling_shot', 'double_tap', 'suppression', 'guardian', 'holo_targeting', 'suppress_fire'].includes(selectedAbility.id);
      
      if (needsTarget && !target) {
        addLog('⚠️ Must select a valid target for this ability');
        return;
      }

      const targets = target ? [target] : [];
      const abilityResult = executeAbility(selectedAbility, selectedUnit, targets, { hasLineOfSight, getCoverBonus, getUnitAt });

      if (abilityResult.success) {
        setUnits(prev => {
          let updated = applyAbilityEffect(abilityResult, prev);
          updated = updated.map(u => 
            u.id === selectedUnit.id 
              ? putAbilityOnCooldown(u, selectedAbility, abilityResult.cooldown || 0)
              : u
          );
          return updated;
        });

        addLog(abilityResult.message);
        if (abilityResult.killMessage) addLog(abilityResult.killMessage);

        setSelectedUnit(prev => ({ ...prev, ap: prev.ap - (abilityResult.apCost || 1) }));

        if (abilityResult.endsPlayerTurn) {
          setTimeout(() => endTurn(), 500);
        }

        setMode('move');
        setSelectedAbility(null);
      } else {
        addLog(abilityResult.message);
      }
    }
  }, [gameOver, selectedUnit, selectedAbility, turn, mode, getUnitAt, hasLineOfSight, getCoverBonus, map, addLog, endTurn, setUnits, setSelectedUnit, setMode, setSelectedAbility]);

  return {
    endTurn,
    handleAbilitySelect,
    handleTileClick,
    getUnitAt,
    hasLineOfSight,
    getCoverBonus
  };
};

export const useEnemyTurn = (turn, units, setUnits, gameOver, endTurn, map, addLog) => {
  useEffect(() => {
    if (turn === UnitType.ENEMY && !gameOver) {
      const timeout = setTimeout(() => {
        const helpers = createCombatHelpers(map, units);
        const activeEnemies = units.filter(u => u.type === UnitType.ENEMY && u.hp > 0 && u.ap > 0);
        const activeSoldiers = units.filter(u => u.type === UnitType.SOLDIER && u.hp > 0);
        
        if (activeEnemies.length > 0 && activeSoldiers.length > 0) {
          const action = executeEnemyAction(
            activeEnemies[0], 
            activeSoldiers, 
            helpers.hasLineOfSight, 
            helpers.getCoverBonus, 
            helpers.getUnitAt
          );
          
          if (action) {
            setUnits(prev => applyEnemyAction(action, prev));
            addLog(action.message);
            if (action.killMessage) addLog(action.killMessage);
          }
        }
        
        const remainingEnemies = units.filter(u => u.type === UnitType.ENEMY && u.hp > 0 && u.ap > 0);
        if (remainingEnemies.length <= 1) {
          setTimeout(() => endTurn(), 1000);
        }
      }, 1000);

      return () => clearTimeout(timeout);
    }
  }, [turn, units, setUnits, gameOver, endTurn, map, addLog]);
};

export const useVictoryCheck = (units, gameOver, setGameOver, setVictory, addLog) => {
  useEffect(() => {
    const conditions = checkVictoryConditions(units);
    if (conditions.gameOver && !gameOver) {
      setGameOver(true);
      setVictory(conditions.victory);
      addLog(conditions.message);
    }
  }, [units, gameOver, addLog, setGameOver, setVictory]);
};

export const useAfterActionNavigation = (navigate, location, activeTheme, activeMission, activeSquad, units, victory, turnCount, campaignId) => {
  const navigateToAfterAction = useCallback(() => {
    let baseMissionXP = XP_VALUES.basic;
    if (activeMission.type === 'campaign' || activeMission.difficulty === 'hard') {
      baseMissionXP = XP_VALUES.campaign;
    } else if (activeMission.type === 'complex' || activeMission.difficulty === 'medium') {
      baseMissionXP = XP_VALUES.complex;
    }

    const casualties = activeSquad.map(soldier => {
      const battleUnit = units.find(u => u.id === soldier.id);
      const survived = battleUnit ? battleUnit.hp > 0 : false;
      const kills = battleUnit?.kills || 0;
      const experienceGained = survived ? baseMissionXP + (kills * KILL_BONUS_XP) : 0;

      return {
        ...soldier,
        survived,
        finalHp: battleUnit ? battleUnit.hp : 0,
        kills,
        experienceGained
      };
    });

    navigate(`/${activeTheme}/after-action`, {
      state: {
        theme: activeTheme, // ADDED: Pass theme to AfterAction
        campaign: location.state?.campaign, // ADDED: Pass full campaign object back
        mission: activeMission,
        campaignId,
        victory,
        casualties,
        turns: turnCount,
        rewards: victory ? activeMission.rewards : { funds: 0, intel: 0, xp: 0 }
      }
    });
  }, [navigate, location, activeTheme, activeMission, activeSquad, units, victory, turnCount, campaignId]);

  return navigateToAfterAction;
};